// Copy text to the clipboard in a way that also works in a non-secure context.
//
// `navigator.clipboard` only exists in a secure context (HTTPS or localhost).
// The iPad debug workflow serves the app over plain HTTP on a LAN hostname
// (e.g. http://peters-macbook-pro.local:3001), where `navigator.clipboard` is
// `undefined` — so the async Clipboard API path must be feature-detected, not
// assumed, and we fall back to the legacy `document.execCommand("copy")` path.
//
// Returns true on success so callers can show their own success/failure
// feedback rather than relying on a thrown error.
export async function copyTextToClipboard(text: string): Promise<boolean> {
  // Secure-context path (production, localhost, desktop): the modern API.
  if (typeof navigator !== "undefined" && navigator.clipboard != null) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the legacy path — the async API can also reject on
      // lost user activation or a restricted context.
    }
  }

  return copyViaExecCommand(text);
}

// Legacy fallback for non-secure contexts. iOS WKWebView/Safari need a real
// selection over the element (not just `.select()`), so build an explicit
// Range and restore whatever the user had selected afterwards.
function copyViaExecCommand(text: string): boolean {
  if (typeof document === "undefined") return false;

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);

  const selection = document.getSelection();
  const savedRange =
    selection != null && selection.rangeCount > 0
      ? selection.getRangeAt(0)
      : null;

  textarea.focus();
  textarea.select();
  const range = document.createRange();
  range.selectNodeContents(textarea);
  selection?.removeAllRanges();
  selection?.addRange(range);
  textarea.setSelectionRange(0, text.length);

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  }

  document.body.removeChild(textarea);
  if (savedRange != null && selection != null) {
    selection.removeAllRanges();
    selection.addRange(savedRange);
  }

  return copied;
}
